// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { UpcomingTrainingAndEventsModel } from "@mcmc/App/Services/UpcomingTrainingAndEventsService";
import { styledBox } from "@mcmc/XDL/Components/Common/Box";
import MultilineEllipsis from "@mcmc/XDL/Components/Common/MultiLineEllipsis";
import Location from "@mcmc/XDL/Icons/Location";
import { mergeWithCss, Roboto, RobotoCondensed } from "@mcmc/XDL/Utilities";
import React, { useState } from "react";
import { css } from "styled-components";

export type Props = UpcomingTrainingAndEventsModel & { extendedStyles?: UpcomingTrainingCardStyles };

export interface UpcomingTrainingCardStyles {
    wrapper?: LinkPresentationProps;
    description?: TypographyPresentationProps;
    courseNumber?: TypographyPresentationProps;
    date?: TypographyPresentationProps;
    locationWrapper?: TypographyPresentationProps;
    locationIcon?: IconProps;
    learnMoreLink?: ClickablePresentationProps;
}

export const upcomingTrainingCardStyles: UpcomingTrainingCardStyles = {
    wrapper: {
        color: "text.main",
        typographyProps: {
            ...Roboto,
            size: 16,
            lineHeight: "19px",
            weight: "normal",
            css: [
                css`
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    justify-content: space-between;
                    height: 100%;
                    width: 100%;
                `,
            ],
        },
        css: css`
            ${styledBox}
            margin-bottom: 0;
            background: ${getColor("common.background")};
            padding: 20px;
            transition: box-shadow 0.2s ease-in-out;

            .read-more-link {
                font-family: ${RobotoCondensed.fontFamily};
                font-weight: bold;
                color: ${getColor("primary")};
            }

            &:hover {
                box-shadow: ${getProp("theme.shadows.2")};
                .read-more-link {
                    color: ${getColor("primary")};
                    text-decoration: underline;
                }
            }
        `,
    },
    description: {
        size: 18,
        lineHeight: "21px",
        weight: 900,
        css: css`
            flex: 1;
        `,
    },
    courseNumber: {
        size: 13,
        lineHeight: "21px",
        color: "secondary",
        weight: "bold",
        css: css`
            letter-spacing: 0;
            margin-bottom: 18px;
        `,
    },
    date: {
        ...RobotoCondensed,
        weight: "bold",
    },

    locationIcon: {
        src: Location,
        color: "primary",
        size: 13,
        css: css`
            margin-right: 5px;
        `,
    },
    locationWrapper: {
        size: 14,
        lineHeight: "21px",
        css: css`
            margin-bottom: 18px;
        `,
    },
    learnMoreLink: {
        css: css`
            text-transform: uppercase;
            margin-bottom: 5px;
        `,
    },
};

const UpcomingTrainingCard: React.FC<Props> = ({
    courseNumber,
    shortDescription,
    urlSegment,
    city,
    state,
    startDate,
    endDate,
    extendedStyles,
}) => {
    const [styles] = useState(() => mergeWithCss(upcomingTrainingCardStyles, extendedStyles));

    return (
        <Link {...styles.wrapper} href={urlSegment}>
            <MultilineEllipsis content={shortDescription} maxLines={2} {...styles.description} />
            <Typography {...styles.courseNumber}>{`${translate("Course #")}: ${courseNumber}`}</Typography>
            <Typography {...styles.date}>
                <LocalizedDateTime
                    dateTime={startDate}
                    options={{ day: "2-digit", month: "2-digit", year: "2-digit" }}
                />
                -
                <LocalizedDateTime dateTime={endDate} options={{ day: "2-digit", month: "2-digit", year: "2-digit" }} />
            </Typography>
            <Typography {...styles.locationWrapper}>
                <Icon {...styles.locationIcon} />
                {city}, {state}
            </Typography>
            <Clickable className="read-more-link" {...styles.learnMoreLink} id={`learnMore_${courseNumber}`}>
                {translate("Learn More")}
            </Clickable>
        </Link>
    );
};

export default UpcomingTrainingCard;
